import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { VoucherHotel } from 'services/ItineraryManagerApi/types';
import { makeBackendApi } from 'services/BackendApi';
import {
  IAnnouncementForVoucherItem,
  IAnnouncementForVoucherHotelRequest,
} from 'services/BackendApi/types/AnnouncementResponse';
import { sortHotelsByEarliestDate } from '../utils';
import { format } from 'date-fns';
import { AnnouncementsDisplayRender } from 'ui/AnnouncementsDisplay';
import { MultiHotelAnnouncementsDisplayRender } from 'ui/AnnouncementsDisplay/MultiHotelAnnouncementsDisplayRender';

interface IVoucherAnnouncementsRendererProps {
  hotels: VoucherHotel[];
}

interface IHotelWithAnnouncements {
  hotelUuid: string;
  hotelName: string;
  announcements: IAnnouncementForVoucherItem[];
}

/**
 * Component to display announcements on the Voucher page
 * - Shows announcements grouped by hotel
 * - Hotels appear in the same order as in the voucher
 * - Announcements within each hotel are sorted by newest created first
 * - Filters based on user type (internal/external)
 * - Uses new POST /announcements/for-voucher endpoint
 */
export const VoucherAnnouncementsRenderer: React.FC<IVoucherAnnouncementsRendererProps> = ({
  hotels,
}) => {
  const backendApi = makeBackendApi();

  // Sort hotels by earliest date to match voucher display order
  const sortedHotels = sortHotelsByEarliestDate(hotels);

  // Prepare hotels data for the API request
  const hotelsForRequest: IAnnouncementForVoucherHotelRequest[] = sortedHotels
    .filter(hotel => hotel.accommodations.some(acc => acc.isVisible))
    .map(hotel => {
      // Get the earliest check-in and latest check-out across all accommodations
      const visibleAccommodations = hotel.accommodations.filter(acc => acc.isVisible);
      const checkInDates = visibleAccommodations.map(acc => new Date(acc.startDate));
      const checkOutDates = visibleAccommodations.map(acc => new Date(acc.endDate));

      const firstCheckIn = checkInDates.sort((a, b) => a.getTime() - b.getTime())[0];
      const lastCheckOut = checkOutDates.sort((a, b) => b.getTime() - a.getTime())[0];

      return {
        hotelUuid: hotel.uuid,
        firstCheckIn: format(firstCheckIn, 'yyyy-MM-dd'),
        lastCheckOut: format(lastCheckOut, 'yyyy-MM-dd'),
      };
    });

  // Fetch announcements using the new endpoint
  const { data, isLoading, isError } = useQuery({
    queryKey: ['voucher-announcements', hotelsForRequest],
    queryFn: async () => {
      try {
        const response = await backendApi.getAnnouncementsForVoucher({
          hotels: hotelsForRequest,
          // treatAsPublic not set - uses authenticated user context
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching announcements for voucher:', error);
        return { data: [] };
      }
    },
    enabled: hotelsForRequest.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  if (isLoading || isError || !data) {
    return null;
  }

  // Group announcements by hotel and sort
  const announcementsByHotel = new Map<string, IAnnouncementForVoucherItem[]>();

  data.data.forEach(announcement => {
    if (!announcementsByHotel.has(announcement.hotelUuid)) {
      announcementsByHotel.set(announcement.hotelUuid, []);
    }
    announcementsByHotel.get(announcement.hotelUuid)!.push(announcement);
  });

  // Sort announcements within each hotel by createdAt descending (newest first)
  announcementsByHotel.forEach((announcements, hotelUuid) => {
    announcements.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  });

  // Create hotels with announcements array in voucher order
  const hotelsWithAnnouncements: IHotelWithAnnouncements[] = sortedHotels
    .filter(hotel => announcementsByHotel.has(hotel.uuid) && announcementsByHotel.get(hotel.uuid)!.length > 0)
    .map(hotel => ({
      hotelUuid: hotel.uuid,
      hotelName: hotel.name,
      announcements: announcementsByHotel.get(hotel.uuid)!,
    }));

  // Calculate total count of announcements
  const totalAnnouncementsCount = hotelsWithAnnouncements.reduce(
    (sum, hotel) => sum + hotel.announcements.length,
    0
  );

  if (totalAnnouncementsCount === 0) {
    return null;
  }

  return (
    <MultiHotelAnnouncementsDisplayRender
      hotelAnnouncements={hotelsWithAnnouncements}
      maxInitialDisplay={3}
    />
  );
};



